<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../Button.svelte';
  import Pagination from '../../Pagination.svelte';
  import SearchInput from '../../SearchInput.svelte';
  import InviteCharityModal from './InviteCharityModal.svelte';
  import Notification from '../../Notification.svelte';
  import EmployeeDonationService, { type ExtendedCharity, type CharityFilters } from '../../../lib/api/employee/donation.service';
  import { apiPageToUiPage } from '../../../lib/utils/pagination';

  // State for charities
  let charities: ExtendedCharity[] = [];
  let loading = false;
  let error: string | null = null;

  // Modal state
  let showInviteModal = false;

  // Notification state
  let notificationShow = false;
  let notificationType: 'success' | 'error' = 'success';
  let notificationMessage = '';

  // Pagination state
  let currentPage = 1;
  let totalPages = 1;
  let itemsPerPage = 20;
  let totalItems = 0;

  // Filter options
  let searchPhrase = '';
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  async function loadCharities() {
    loading = true;
    error = null;
    
    try {
      const filters: CharityFilters = {
        page: currentPage,
        per_page: itemsPerPage,
        verified: true,
        include_my_invited: true,
        sort_by: 'name',
        sort_direction: 'asc'
      };

      if (searchPhrase) {
        filters.phrase = searchPhrase;
      }

      const response = await EmployeeDonationService.getCharities(filters);
      
      if (response.success && response.data) {
        charities = response.data;
        
        if (response.paging && typeof response.paging === 'object') {
          currentPage = apiPageToUiPage(response.paging.current_page);
          totalPages = response.paging.last_page ?? 1;
          totalItems = response.paging.total ?? 0;
        }
      } else {
        error = response.message || 'Failed to load charities';
        charities = [];
      }
    } catch (err: any) {
      console.error('Error loading charities:', err);
      error = err.message || 'An unexpected error occurred';
      charities = [];
    } finally {
      loading = false;
    }
  }

  function handleSearch() {
    // Debounce search input
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    debounceTimer = setTimeout(() => {
      currentPage = 1; // Reset to first page when searching
      loadCharities();
    }, 300);
  }

  function handlePageChange(event: CustomEvent<{ page: number }>) {
    currentPage = event.detail.page;
    loadCharities();
  }

  function handleCharityClick(charity: ExtendedCharity) {
    if (charity.uuid) {
      push(`/view-charity/${charity.uuid}`);
    }
  }

  function handleInviteComplete() {
    showInviteModal = false;
    notificationMessage = 'Charity invitation sent successfully! They will receive an email to complete registration.';
    notificationType = 'success';
    notificationShow = true;
    loadCharities();
  }

  function handleInviteCancel() {
    showInviteModal = false;
  }

  // Load charities on mount
  onMount(() => {
    loadCharities();
  });
</script>

<div class="charities-page">
  <!-- Filters Section -->
  <div class="filters-section">
    <div class="filters-left">
      <SearchInput 
        placeholder="Search charities by name..." 
        bind:value={searchPhrase}
        on:search={handleSearch}
        on:input={handleSearch}
        width="250px"
      />
    </div>
    <div class="filters-right">
      <Button variant="primary" on:click={() => showInviteModal = true}>
        <div class="button-content">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          <span>Invite Charity</span>
        </div>
      </Button>
    </div>
  </div>

  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading charities...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <p class="error-message">{error}</p>
      <Button variant="primary" on:click={loadCharities}>Try Again</Button>
    </div>
  {:else if charities.length === 0}
    <div class="empty-state">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="empty-icon">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
      </svg>
      <h3>No Charities Found</h3>
      <p>{searchPhrase ? 'Try adjusting your search terms.' : 'No charities are available at this time. Invite a charity to get started!'}</p>
      <div class="empty-actions">
        {#if searchPhrase}
          <Button variant="secondary" on:click={() => { searchPhrase = ''; loadCharities(); }}>
            Clear Search
          </Button>
        {/if}
        <Button variant="primary" on:click={() => showInviteModal = true}>
          Invite Charity
        </Button>
      </div>
    </div>
  {:else}
    <div class="charities-grid">
      {#each charities as charity (charity.uuid)}
        <div 
          class="charity-card"
          on:click={() => handleCharityClick(charity)}
          role="button"
          tabindex="0"
          on:keydown={(e) => e.key === 'Enter' && handleCharityClick(charity)}
        >
          <div class="charity-header">
            <div class="charity-info">
              <h3 class="charity-name">{charity.name}</h3>
              {#if charity.category?.name}
                <span class="charity-category">{charity.category.name}</span>
              {/if}
            </div>
            {#if charity.verified}
              <span class="status-badge status-verified">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                Verified
              </span>
            {:else}
              <span class="status-badge status-pending">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
                Invited
              </span>
            {/if}
          </div>

          <div class="charity-details">
            {#if charity.abn}
              <div class="detail-row">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="7" y1="8" x2="17" y2="8"></line>
                  <line x1="7" y1="12" x2="17" y2="12"></line>
                  <line x1="7" y1="16" x2="12" y2="16"></line>
                </svg>
                <span class="detail-text">ABN: {charity.abn}</span>
              </div>
            {/if}

            {#if charity.contact_email}
              <div class="detail-row">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <span class="detail-text">{charity.contact_email}</span>
              </div>
            {/if}

            {#if charity.head_office_location}
              <div class="detail-row">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span class="detail-text">{charity.head_office_location}</span>
              </div>
            {/if}
          </div>

          <div class="charity-footer">
            <div class="view-link">
              View Details
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
            </div>
          </div>
        </div>
      {/each}
    </div>

    {#if totalPages > 1}
      <div class="pagination-container">
        <Pagination 
          currentPage={currentPage} 
          totalPages={totalPages} 
          on:pageChange={handlePageChange} 
        />
      </div>
    {/if}
  {/if}
</div>

<!-- Invite Charity Modal -->
<InviteCharityModal 
  bind:isOpen={showInviteModal}
  on:complete={handleInviteComplete}
  on:cancel={handleInviteCancel}
/>

<!-- Notification Component -->
<Notification
  show={notificationShow}
  type={notificationType}
  message={notificationMessage}
  onDismiss={() => { notificationShow = false; }}
/>

<style>
  .charities-page {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
    background-color: #f7fafc;
    min-height: 100vh;
  }

  .filters-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;
    background: white;
    padding: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    width: 100%;
  }

  .filters-left {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex: 1;
  }

  .filters-right {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex-shrink: 0;
  }

  .button-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    justify-content: center;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid var(--color-primary, #f0b429);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .error-message {
    color: #dc2626;
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    text-align: center;
  }

  .empty-icon {
    color: #d1d5db;
    margin-bottom: 1.5rem;
  }

  .empty-state h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  .empty-state p {
    color: #6b7280;
    margin-bottom: 1.5rem;
    max-width: 400px;
    font-size: 0.875rem;
  }

  .empty-actions {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  .charities-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 1rem;
    margin-bottom: 2rem;
  }

  .charity-card {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 1.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .charity-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
    border-color: var(--color-primary, #f0b429);
  }

  .charity-card:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.1);
  }

  .charity-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
  }

  .charity-info {
    flex: 1;
    min-width: 0;
  }

  .charity-name {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
    line-height: 1.4;
  }

  .charity-category {
    font-size: 0.75rem;
    color: #6b7280;
    background: #f3f4f6;
    padding: 0.125rem 0.5rem;
    border-radius: 4px;
    display: inline-block;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .status-verified {
    background: #d1fae5;
    color: #065f46;
  }

  .status-pending {
    background: #fef3c7;
    color: #92400e;
  }

  .charity-details {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .detail-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #6b7280;
  }

  .detail-text {
    color: #374151;
    font-weight: 400;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .charity-footer {
    margin-top: auto;
    padding-top: 1rem;
    border-top: 1px solid #f3f4f6;
  }

  .view-link {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    color: var(--color-primary, #3b82f6);
    font-size: 0.875rem;
    font-weight: 500;
    transition: all 0.2s;
  }

  .charity-card:hover .view-link {
    color: var(--color-primary-hover, #2563eb);
  }

  .view-link svg {
    transition: transform 0.2s;
  }

  .charity-card:hover .view-link svg {
    transform: translate(2px, -2px);
  }

  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
  }

  /* Responsive design */
  @media (max-width: 768px) {
    .charities-page {
      padding: 1rem;
    }

    .charities-grid {
      grid-template-columns: 1fr;
    }

    .filters-section {
      flex-direction: column;
      gap: 1rem;
      align-items: stretch;
    }

    .filters-left {
      flex-direction: column;
      gap: 0.75rem;
    }

    .filters-right {
      width: 100%;
    }

    .filters-right :global(button) {
      width: 100%;
    }
  }
</style>


